import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useBooleanControl } from 'components/modules/Insurance/hooks/useBooleanControl';
import {
  getChangeEvent,
  getPlaneChangeEvent,
} from 'components/modules/Insurance/Insurance.helpers';
import { insAnketaDeleteDriver } from 'store/insuranceAnketa';
import { DRIVER_FIELDS } from 'store/insuranceAnketa/constants';
import { selectDriver } from 'store/insuranceAnketa/selectors';

import { CONFIRM_MESSAGE } from '../../Drivers.constants';
import { useDisabledFields } from '../../hooks/useDisabledFields';

import { useEdit } from './hooks/useEdit';
import { useErrors } from './hooks/useErrors';
import { useHeader } from './hooks/useHeader';
import { useLocalState } from './hooks/useLocalState';
import { useSubmit } from './hooks/useSubmit';

export const useDriverCardPresenter = ({ id }) => {
  const dispatch = useDispatch();

  const { [DRIVER_FIELDS.EMPTY]: empty } = useSelector(selectDriver(id));

  const {
    onDisable: onClosePopup,
    onEnable: onOpenPopup,
    value: isPopupVisible,
  } = useBooleanControl();

  const { disabled: pageRestrict } = useDisabledFields();

  const { editing, onSetEdit } = useEdit({ id });
  const { errors, onSetError, onSetErrors } = useErrors({ id });
  const { description, title } = useHeader({ id });
  const { changed, onReset, onSetLocal, values } = useLocalState({ id });
  const { onSubmit } = useSubmit({ id, values });

  const onChange = useCallback(
    (event) => {
      const { key, value } = getChangeEvent(event);

      onSetLocal({ key, value });
      onSetError({ key, value: '' });
    },
    [onSetLocal, onSetError]
  );
  const onChangeGender = useCallback(
    (event) => {
      const key = DRIVER_FIELDS.GENDER;
      const value = getPlaneChangeEvent(event);

      onSetLocal({ key, value });
    },
    [onSetLocal]
  );
  const onDeleteDriver = useCallback(() => {
    dispatch(insAnketaDeleteDriver({ id }));
  }, [id]);
  const onDelete = useCallback(() => {
    onClosePopup();
    onDeleteDriver();
  }, [onClosePopup, onDeleteDriver]);
  const onEndEdit = useCallback(() => {
    // Если водитель есть в store, то просто закрываем блок
    if (!empty) {
      onSetEdit(false);

      return;
    }

    if (changed) {
      const confirmed = window.confirm(CONFIRM_MESSAGE);

      // Если пользователь не согласился с тем что водитель будет удален, то ничего не делаем
      if (!confirmed) {
        return;
      }
    }

    onDeleteDriver();
  }, [empty, onSetEdit, changed, onDeleteDriver]);
  const onSave = useCallback(async () => {
    try {
      await onSubmit();
      onSetEdit(false);
    } catch (errors) {
      onSetErrors(errors);
    }
  }, [onSubmit, onSetEdit, onSetErrors]);
  const onStartEdit = useCallback(() => {
    // Восстанавливаем значения из store
    onReset();
    onSetEdit(true);
  }, [onReset, onSetEdit]);

  return {
    changed,
    description,
    disabled: pageRestrict,
    editing,
    empty,
    errors,
    isPopupVisible,
    onChange,
    onChangeGender,
    onClosePopup,
    onDelete,
    onEndEdit,
    onOpenPopup,
    onSave,
    onStartEdit,
    title,
    values,
  };
};
